import { Above, Below } from '@jetshop/ui/Breakpoints';
import PaginationContext from '@jetshop/core/components/Pagination/PaginationContext';
import { PaginationBehaviour } from '@jetshop/ui/Pagination';
import { styled } from 'linaria/react';
import React, { useEffect, useState } from 'react';
import CategoryHeader from '../CategoryPage/CategoryHeader';
import MaxWidth from '../Layout/MaxWidth';
import { Pagination } from '../Pagination/Pagination';
import { theme } from '../Theme';
import CategoryHeaderLoadingState from './CategoryHeaderLoadingState';
import Filters from './Filters';
import ProductGrid from './ProductGrid';
import ClubList from './ClubList';
import InspirationList from '../Inspiration/InspirationList';
import InspirationPage from '../Inspiration/InspirationPage';
import ContentCategoryPage from '../ContentCategoryPage/ContentCategoryPage';
import { useApolloClient, useQuery } from 'react-apollo';
import catProdQuery from './CatProductsQueryByRoute.gql';
import { useLocation, useHistory } from 'react-router-dom';
import { useFilterByStore } from './useFilterByStore';
import { removeStoreIdFromUrl } from './StoreFilterOptions';
import useLocalStorageStoreFilterActivated from './useLocalStorageStoreFilterActivated';
import GetStores from './GetStores.gql';
import t from '@jetshop/intl';

const Container = styled(MaxWidth)`
  padding-top: 2rem;
  display: flex;
  flex-direction: row;
  ${theme.below.lg} {
    display: block;
    padding-top: 10px;
  }
`;

const Left = styled('div')`
  width: 250px;
  flex: 1;
  ${theme.below.lg} {
    width: 100%;
    > div {
      display: block;
    }
  }
`;
const Right = styled('div')`
  flex: 4;
`;

const CategoryWrapper = styled('div')``;
const CategoryBottom = styled('div')`
  margin: 20px 0;
  width: 100%;
  border-top: 1px solid #e0e0e0;
  padding-top: 30px;
  a {
    color: ${theme.colors.primary};
  }
`;
const CategoryContent = styled('div')`
  font-size: 13px;
  line-height: 1.6;
`;

const StoreFilterResults = styled('div')`
  display: flex;
  justify-content: space-between;
  padding-bottom: 0.5rem;
`;

const Category = ({ category, result }) => {
  const location = useLocation();
  const [allProducts, setAllProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState([]);
  const [totalResults] = useState(0);
  const [activeStoreFilter, setActiveStoreFilter] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [storeId, setStoreId] = useState(null);
  const limit = 100;
  const client = useApolloClient();
  const history = useHistory();
  const params = new URLSearchParams(location.search)?.get('storeId');
  const [activeStore, setActiveStore] = useState(null);

  // Handle localStorage safely
  const [isStoreFilterActivated] = useLocalStorageStoreFilterActivated();

  const { data: storeData } = useQuery(GetStores, {
    variables: { includeInactive: false }
  });

  const resultLimit = 500;

  const filterByStore = useFilterByStore(
    totalProducts,
    category?.products?.filters,
    setAllProducts
  );

  useEffect(() => {
    let isStoreIdRemoved = false;
    if (category?.products?.totalResults > resultLimit) {
      setActiveStoreFilter(false);
      setStoreId(undefined);
    }

    const sizeActive = category?.products?.filters?.some(filter => {
      return filter.name === 'Størrelse' && filter.hasActiveItems === true;
    });
    const stockActive = category?.products?.filters?.some(
      filter => filter.name === 'På lager' && filter.value === false
    );

    if (params) {
      setStoreId(params);
      const store =
        storeData.stores.find(store => store.id === parseInt(params)) || false;

      if (store) {
        setActiveStore(store);
      }

      if (
        category?.products?.totalResults < resultLimit &&
        isStoreFilterActivated &&
        store
      ) {
        if (!sizeActive || !stockActive) {
          setActiveStoreFilter(false);
          if (params && !isStoreIdRemoved) {
            removeStoreIdFromUrl(history, location);
            isStoreIdRemoved = true; // Set the flag to true to avoid further calls
          }
        } else {
          setActiveStoreFilter(true);
        }
      }
    }
  }, [params, category, isStoreFilterActivated]);

  useEffect(() => {
    if (activeStoreFilter && isStoreFilterActivated) {
      const loadAllProducts = async () => {
        setAllProducts([]);
        setTotalProducts([]);
        setLoadingProducts(true);

        try {
          // Fetch the total count of products first
          const { data: initialData } = await client.query({
            query: catProdQuery,
            variables: {
              path: location.pathname,
              first: 1,
              offset: 0,
              filters: result.variables.filters
            }
          });

          const totalResults =
            initialData?.route?.object?.products?.totalResults || 0;
          const limit = 100;
          const totalPages = Math.ceil(totalResults / limit);

          // Generate all offsets
          const offsets = Array.from(
            { length: totalPages },
            (_, index) => index * limit
          );

          // Fetch all pages concurrently
          const allResults = await Promise.all(
            offsets.map(offset =>
              client.query({
                query: catProdQuery,
                variables: {
                  path: location.pathname,
                  first: limit,
                  offset: offset,
                  filters: result.variables.filters
                }
              })
            )
          );

          // Combine results
          const allItems = allResults.flatMap(
            result => result.data?.route?.object?.products?.result || []
          );

          setAllProducts(allItems);
          setTotalProducts(allItems);
        } catch (error) {
          console.error('Error fetching products:', error);
        } finally {
          setLoadingProducts(false);
        }
      };

      loadAllProducts();
    }
  }, [totalResults, limit, client, activeStoreFilter, category]);

  useEffect(() => {
    if (totalProducts.length > 0 && storeId) {
      filterByStore(storeId);
    }
  }, [totalProducts, filterByStore, storeId]);

  if (!category) {
    return (
      <CategoryWrapper>
        <CategoryHeaderLoadingState />;
      </CategoryWrapper>
    );
  }

  const clubListCategories = [231, 161, 222, 224, 1271];
  const firstLevelList = [224];
  if (clubListCategories.includes(category?.id)) {
    return (
      <ClubList
        category={category}
        firstLevel={firstLevelList.includes(category?.id) ? true : false}
      />
    );
  }
  if (
    category?.id === 239 ||
    category?.id === 1118 ||
    category?.id === 1119 ||
    category?.id === 1172 ||
    category?.id === 559
  ) {
    var title = 'GUIDE';
    if (category?.id === 559) {
      title = 'Kolleksjon';
    }
    if (category?.id === 1172) {
      title = 'ANMELDELSE';
    }
    return <InspirationList title={title} category={category} />;
  }
  if (
    category?.parent?.id === 239 ||
    category?.parent?.id === 1118 ||
    category?.parent?.id === 1119 ||
    category?.parent?.id === 1172
  ) {
    return (
      <InspirationPage
        parents={result?.data?.route?.parents}
        category={category}
      />
    );
  }

  if (
    category?.products?.filters?.length === 0 &&
    category?.products?.result?.length === 0 &&
    (category?.id === 165 ||
      category?.parent?.id === 165 ||
      category?.parent?.id === 567 ||
      category?.parent?.id === 575 ||
      category?.parent?.id === 1038)
  ) {
    return (
      <ContentCategoryPage
        parents={result?.data?.route?.parents}
        category={category}
      />
    );
  }

  return (
    <CategoryWrapper>
      <>
        {category?.products?.totalResults !== 0 && (
          <>
            <CategoryHeader
              category={category}
              parents={result?.data?.route?.parents}
            />
          </>
        )}

        {activeStoreFilter && (
          <h2 style={{ textAlign: 'center' }}>{activeStore.name}</h2>
        )}

        <Container>
          {category?.products?.filters?.length > 0 && (
            <Above breakpoint="lg">
              {matches =>
                matches && (
                  <Left>
                    <Filters
                      filters={category.products?.filters || []}
                      sortOrders={category.products?.sortOrders || []}
                      result={result}
                    />
                  </Left>
                )
              }
            </Above>
          )}

          <Right>
            <div>
              {category?.products?.filters?.length > 0 && (
                <Below breakpoint="lg">
                  {matches =>
                    matches && (
                      <Left>
                        <Filters
                          filters={category.products?.filters || []}
                          sortOrders={category.products?.sortOrders || []}
                          result={result}
                        />
                      </Left>
                    )
                  }
                </Below>
              )}

              {activeStoreFilter ? (
                loadingProducts ? (
                  <p>{t('Loading')}...</p>
                ) : (
                  !allProducts.length && (
                    <h3 style={{ margin: '0 auto' }}>{t('No results')}</h3>
                  )
                )
              ) : (
                ''
              )}
              <StoreFilterResults>
                {activeStoreFilter && allProducts.length >= 1 && (
                  <p>Product listed: {allProducts.length}</p>
                )}
                {activeStoreFilter && !loadingProducts && (
                  <p>Total results: {category?.products?.totalResults}</p>
                )}
              </StoreFilterResults>
              <>
                <ProductGrid
                  products={
                    // sets the store filter result if active
                    activeStoreFilter ? allProducts : category.products.result
                  }
                  listName={category.name}
                  categoryPath={
                    category.isDynamic ? null : result?.data?.route ?? null
                  }
                  loading={loadingProducts || result.loading}
                  imageSizes={[1 / 2, 1 / 2, 1 / 3, 1 / 4]}
                  buyable={false}
                  className="product-list"
                />
              </>
            </div>

            {/* remove pagination if storefilter is active */}
            {!activeStoreFilter && (
              <PaginationContext.Consumer>
                {props => (
                  <PaginationBehaviour
                    {...props}
                    total={category.products.totalResults}
                  >
                    {props =>
                      category.products.totalResults > 0 ? (
                        <Pagination {...props} />
                      ) : null
                    }
                  </PaginationBehaviour>
                )}
              </PaginationContext.Consumer>
            )}

            {category?.content?.length > 0 && (
              <CategoryBottom>
                <CategoryContent
                  data-testid="category-description"
                  dangerouslySetInnerHTML={{
                    __html: category.content
                  }}
                />
              </CategoryBottom>
            )}
          </Right>
        </Container>
      </>
    </CategoryWrapper>
  );
};

export default Category;
